import React, { useEffect, useState, useRef } from 'react';
import { Button, Card } from 'antd';
import 'antd/dist/reset.css';
import { type } from '@testing-library/user-event/dist/type';
import { useSwipeable } from 'react-swipeable';

const GRID_SIZE = 4;
const TARGET_PRIME = 2357;
const SPAWN_NUMBERS = [2, 3, 4, 6, 8];
const PRIMES = generatePrimesUpTo(TARGET_PRIME + 100);

function generatePrimesUpTo(max) {
  const sieve = Array(max + 1).fill(true);
  sieve[0] = sieve[1] = false;
  for (let i = 2; i * i <= max; i++) {
    if (sieve[i]) {
      for (let j = i * i; j <= max; j += i) {
        sieve[j] = false;
      }
    }
  }
  return sieve.map((val, i) => val ? i : null).filter(Boolean);
}

function nextPrime(n) {
  for (let i = 0; i < PRIMES.length - 1; i++) {
    if (PRIMES[i] === n) return PRIMES[i + 1];
  }
  return null;
}

function getRandomSpawnNumber() {
    const sequence = [2, 4, 6, 8];
    if (!getRandomSpawnNumber.counter) {
        getRandomSpawnNumber.counter = 0;
    }
    if (!getRandomSpawnNumber.threeCounter) {
        getRandomSpawnNumber.threeCounter = 0;
    }

    if (getRandomSpawnNumber.threeCounter === 2) {
        getRandomSpawnNumber.threeCounter = 0;
        return 3;
    }

    const number = sequence[getRandomSpawnNumber.counter % sequence.length];
    getRandomSpawnNumber.counter++;
    if (getRandomSpawnNumber.counter % sequence.length === 0) {
        getRandomSpawnNumber.threeCounter++;
    }
    return number;
}

function initializeGrid() {
    const grid = Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(null));
    const initialValues = [2, 3, 5, 7, 2];
    let index = 0;

    for (let r = 0; r < GRID_SIZE; r++) {
        for (let c = 0; c < GRID_SIZE; c++) {
            if (index < initialValues.length) {
                grid[r][c] = initialValues[index];
                index++;
            } else {
                break;
            }
        }
        if (index >= initialValues.length) break;
    }

    return grid;
}

function initializeGridWithPrimeTiles() {
  const initialGrid = initializeGrid();
  const { updatedGrid, primeTileInfo, primeTileMap } = updatePrimeTiles(initialGrid);
  return { grid: updatedGrid, primeTileInfo, primeTileMap };
}

function spawnTile(grid, primeTileInfo, primeTileMap) {
  const emptyCells = [];
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      if (!grid[r][c]) emptyCells.push([r, c]);
    }
  }
  if (emptyCells.length === 0) return;

  const [r, c] = emptyCells[Math.floor(Math.random() * emptyCells.length)];

  // Check if there is an active prime tile
  const activePrimeTile = primeTileInfo.find(tile => tile.isActive);
  if (activePrimeTile) {
    const nonPrimeTile = {
      value: getRandomSpawnNumber(),
      isPrimeTile: true,
      targetPrime: activePrimeTile.targetPrime,
      maxTotal: activePrimeTile.targetPrime - activePrimeTile.value, // Link to the prime prime tile
      primeTileId: activePrimeTile.primeTileId, // Link to the prime prime tile ID
      type: 'non-prime', // Type for non-prime prime tile
    };

    // Ensure the value is always even
    if (nonPrimeTile.value % 2 !== 0) {
      nonPrimeTile.value += 1;
    }
    grid[r][c] = nonPrimeTile;
    primeTileMap.get(activePrimeTile.primeTileId).push(nonPrimeTile); // Add to the map
    // Deactivate the prime tile after spawning one prime tile
    activePrimeTile.isActive = false;
  } else {
    grid[r][c] = getRandomSpawnNumber();
  }
}

function cloneGrid(grid) {
  return grid.map(row => row.slice());
}

function updatePrimeTiles(grid) {
  const updatedGrid = cloneGrid(grid);
  const primeTileInfo = [];
  const primeTileMap = new Map(); // Map to link prime prime tiles and their corresponding non-prime prime tiles

  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      const cell = grid[r][c];
      if (cell && PRIMES.includes(cell)) {
        const next = nextPrime(cell);
        if (next && next - cell > 8) {
          const primeTileId = `${r}-${c}`; // Unique ID for the prime prime tile
          updatedGrid[r][c] = {
            value: cell,
            isPrimeTile: true,
            targetPrime: next,
            primeTileId, // Assign unique ID
            type: 'prime', // Type for prime prime tile
          };
          primeTileInfo.push({ row: r, col: c, targetPrime: next, value: cell, isActive: true, primeTileId });
          primeTileMap.set(primeTileId, []); // Initialize the map entry for this prime prime tile
        }
      }
    }
  }

  return { updatedGrid, primeTileInfo, primeTileMap };
}

function mergeTiles(row, primeTileInfo, primeTileMap) {
  const newRow = row.filter(Boolean);
  for (let i = 0; i < newRow.length - 1; i++) {
    const current = newRow[i];
    const next = newRow[i + 1];
    const sum = (current?.value || current) + (next?.value || next);

    if (current?.isPrimeTile && next?.isPrimeTile) {
      // Ensure tiles are linked to the same prime prime tile
      if (current.primeTileId === next.primeTileId && (sum === nextPrime(current.value || current) || sum === nextPrime(next.value || next)) ) {
        newRow[i] = { value: sum }; // Merge two prime tiles to form the next prime
        newRow.splice(i + 1, 1);
      }
    } else if (current?.isPrimeTile && sum === current.targetPrime && current.type === "prime") {
      newRow[i] = { value: sum }; // Merge to form the next prime
      newRow.splice(i + 1, 1);
    } else if (current?.isPrimeTile && current.type === "non-prime" && sum <= current.maxTotal && sum % 2 === 0) {
      newRow[i] = { ...current, value: sum }; // Allow merging even numbers up to the target prime
      newRow.splice(i + 1, 1);
    } else if (next?.isPrimeTile && sum === next.targetPrime) {
      newRow[i] = { value: sum }; // Merge to form the next prime
      newRow.splice(i + 1, 1);
    } else if (next?.isPrimeTile && next.type === "non-prime" && sum <= next.maxTotal && sum % 2 === 0) {
      newRow[i + 1] = { ...next, value: sum }; // Allow merging even numbers up to the target prime
      newRow.splice(i, 1);
    } else if (PRIMES.includes(sum) && (!current?.isPrimeTile && !next?.isPrimeTile)) {
      newRow[i] = sum; // Regular prime merge
      newRow.splice(i + 1, 1);
    } else if (!current?.isPrimeTile && !next?.isPrimeTile && current % 2 === 0 && next % 2 === 0 && current === next) {
      newRow[i] = current + next; // Merge two even non-prime tiles
      newRow.splice(i + 1, 1);
    }
  }
  while (newRow.length < GRID_SIZE) newRow.push(null);
  return newRow;
}

function move(grid, direction, primeTileInfo, primeTileMap) {
  let moved = false;
  let newGrid = cloneGrid(grid);

  for (let i = 0; i < GRID_SIZE; i++) {
    let rowOrCol = [];
    for (let j = 0; j < GRID_SIZE; j++) {
      rowOrCol.push(
        direction === 'left' || direction === 'right'
          ? newGrid[i][j]
          : newGrid[j][i]
      );
    }
    if (direction === 'right' || direction === 'down') rowOrCol.reverse();
    rowOrCol = mergeTiles(rowOrCol, primeTileInfo, primeTileMap);
    if (direction === 'right' || direction === 'down') rowOrCol.reverse();

    for (let j = 0; j < GRID_SIZE; j++) {
      if (direction === 'left' || direction === 'right') {
        if (newGrid[i][j] !== rowOrCol[j]) moved = true;
        newGrid[i][j] = rowOrCol[j];
      } else {
        if (newGrid[j][i] !== rowOrCol[j]) moved = true;
        newGrid[j][i] = rowOrCol[j];
      }
    }
  }

  if (moved) spawnTile(newGrid, primeTileInfo, primeTileMap);
  return [newGrid, moved];
}

function isGameOver(grid) {
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      if (!grid[r][c]) return false; // Empty cell exists
      if (c < GRID_SIZE - 1 && PRIMES.includes(grid[r][c] + grid[r][c + 1])) return false; // Mergeable horizontally
      if (r < GRID_SIZE - 1 && PRIMES.includes(grid[r][c] + grid[r + 1][c])) return false; // Mergeable vertically
    }
  }
  return true;
}

export default function PrimePathGame() {
  const [grid, setGrid] = useState(initializeGridWithPrimeTiles);
  const boardRef = useRef(null);

  // Extracted move logic for both keyboard and swipe
  const triggerMove = (direction) => {
    const [newGrid, moved] = move(grid.grid, direction, grid.primeTileInfo, grid.primeTileMap);
    if (moved) {
      const { updatedGrid, primeTileInfo, primeTileMap } = updatePrimeTiles(newGrid);
      setGrid({ grid: updatedGrid, primeTileInfo, primeTileMap });
      if (isGameOver(updatedGrid)) {
        alert('Game Over!');
      }
    }
  };

  const handleKey = (e) => {
    const dirKeys = {
      ArrowLeft: 'left',
      ArrowRight: 'right',
      ArrowUp: 'up',
      ArrowDown: 'down',
    };
    if (dirKeys[e.key]) {
      triggerMove(dirKeys[e.key]);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [grid]);

  // Use react-swipeable for swipe gestures
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => triggerMove('left'),
    onSwipedRight: () => triggerMove('right'),
    onSwipedUp: () => triggerMove('up'),
    onSwipedDown: () => triggerMove('down'),
    trackTouch: true,
    trackMouse: false,
    preventDefaultTouchmoveEvent: true,
  });

  const handleRestart = () => {
    setGrid(initializeGridWithPrimeTiles());
  };

  const getHighestPrime = () => {
    let highestPrime = 0;
    grid.grid.forEach(row => {
      row.forEach(cell => {
        const value = cell?.value || cell;
        if (PRIMES.includes(value) && value > highestPrime) {
          highestPrime = value;
        }
      });
    });
    return highestPrime;
  };

  const getNextPrimes = (start, count) => {
    const startIndex = PRIMES.indexOf(start);
    return PRIMES.slice(startIndex + 1, startIndex + 1 + count);
  };

  const highestPrime = getHighestPrime();
  const nextPrimes = getNextPrimes(highestPrime, 5);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-gray-100 p-4">
      <h1 className="text-2xl md:text-4xl font-extrabold text-purple-400 mb-4 drop-shadow-md text-center">Prime Path Demo</h1>
      
      {/* Link back to Prime Path App page */}
      <div className="mb-8 text-center">
        <p className="text-gray-300 mb-3">Try this web demo, then get the full mobile experience!</p>
        <button
          onClick={() => {
            window.location.href = '/prime-path';
          }}
          className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-full shadow-lg transition duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-500 focus:ring-opacity-50 flex items-center justify-center mx-auto"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
          </svg>
          Download Mobile App
        </button>
      </div>

      <div className="flex flex-col md:flex-row items-center md:items-start">
        {/* Game Board */}
        <div
          className="grid grid-cols-4 gap-2 p-2 bg-gray-800 rounded-lg shadow-xl border border-gray-700"
          ref={boardRef}
          style={{ touchAction: 'none' }}
          {...swipeHandlers}
        >
          {grid.grid.map((row, rowIndex) =>
            row.map((cell, colIndex) => {
              const cellValue = cell?.value || cell;
              const isPrimeTile = cell?.isPrimeTile;
              const targetInfo = cell?.maxTotal || nextPrime(cellValue);

              return (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className={`
                  w-16 h-16 md:w-20 md:h-20 flex flex-row items-center justify-center rounded-lg shadow-md
                  border-2 border-gray-600 relative
                  ${isPrimeTile ? 'bg-purple-700' : 'bg-gray-700'}
                  ${cellValue === TARGET_PRIME ? 'bg-yellow-500 text-gray-900' : ''}
                  `}
                >
                  <div className="text-sm md:text-xl font-bold text-white">{cellValue || ''}</div>
                  {cellValue !== null && (
                    <div className="absolute bottom-1 right-1 text-xs text-gray-400">
                      {targetInfo || ''}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Next Primes and Game Rules */}
        <div className="mt-8 md:mt-0 md:ml-8 flex flex-col items-center md:items-start">
          {/* Next Primes */}
          <div className="mb-4 p-2 bg-gray-800 rounded-lg shadow-xl border border-gray-700 w-full md:w-64">
            <h2 className="text-xl md:text-2xl font-bold text-purple-400 mb-4 text-center md:text-left">Next Primes</h2>
            <div className="flex space-x-2 justify-center md:justify-start">
              {nextPrimes.map((prime, index) => (
                <div
                  key={index}
                  className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center bg-purple-600 text-white font-bold rounded-md shadow-md"
                >
                  {prime}
                </div>
              ))}
            </div>
          </div>

          {/* Game Rules */}
          <div className="p-2 bg-gray-800 rounded-lg shadow-xl border border-gray-700 w-full md:w-64">
            <h2 className="text-xl md:text-2xl font-bold text-purple-400 mb-4 text-center md:text-left">Game Rules</h2>
            <ul className="list-disc list-inside text-gray-200 text-xs md:text-sm">
              <li>Use arrow keys to move tiles.</li>
              <li>Merge tiles to form the next prime number.</li>
              <li>Non-prime tiles can merge if they are even and equal.</li>
              <li>Reach the target prime number 2357 to win.</li>
              <li>Game ends when no moves are possible.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-4 items-center">
        <button
          onClick={handleRestart}
          className="px-4 py-2 md:px-6 md:py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-indigo-500 focus:ring-opacity-75"
        >
          Restart Game
        </button>
        
        <button
          onClick={() => {
            window.location.href = '/prime-path';
          }}
          className="px-4 py-2 md:px-6 md:py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-500 focus:ring-opacity-75"
        >
          Get Mobile App
        </button>
      </div>
      
      {/* Additional info section */}
      <div className="mt-6 text-center max-w-md">
        <p className="text-gray-400 text-sm">
          Enjoying the demo? The mobile app includes achievements, better touch controls, 
          and an optimized gaming experience.
        </p>
      </div>
    </div>
  );
}
